import { supabase } from '../lib/supabase';

/**
 * Unified Inventory Service
 * Manages barrel_inventory, carton_inventory, inventory_movements
 */
export const inventoryService = {

  // ===================== BARREL INVENTORY =====================

  async getBarrelInventory() {
    try {
      const { data, error } = await supabase
        ?.from('barrel_inventory')
        ?.select('*, carton_suppliers(id, name)')
        ?.order('barrel_type', { ascending: true });
      if (error) throw error;
      return data?.map(item => ({
        id: item?.id,
        name: item?.name || item?.barrel_type,
        barrelType: item?.barrel_type,
        category: item?.category || 'barrique',
        sku: item?.sku,
        description: item?.description,
        imageUrl: item?.image_url,
        purchasePrice: item?.purchase_price || 0,
        salePrice: item?.price_euros || 0,
        priceEuros: item?.price_euros || 0,
        stockQuantity: item?.stock_quantity || 0,
        minStock: item?.min_stock || 5,
        reorderThreshold: item?.reorder_threshold || 10,
        location: item?.location || 'Entrepôt principal',
        supplierId: item?.supplier_id,
        supplierName: item?.carton_suppliers?.name,
        status: item?.status || 'active',
        archivedAt: item?.archived_at,
        lastRestockedAt: item?.last_restocked_at,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at,
        tableSource: 'barrel_inventory'
      })) || [];
    } catch (error) {
      console.error('Error fetching barrel inventory:', error);
      throw error;
    }
  },

  async updateBarrelInventoryItem(id, itemData) {
    try {
      const updatePayload = {};
      if (itemData?.name !== undefined) updatePayload.name = itemData?.name;
      if (itemData?.category !== undefined) updatePayload.category = itemData?.category;
      if (itemData?.sku !== undefined) updatePayload.sku = itemData?.sku;
      if (itemData?.description !== undefined) updatePayload.description = itemData?.description;
      if (itemData?.imageUrl !== undefined) updatePayload.image_url = itemData?.imageUrl;
      if (itemData?.purchasePrice !== undefined) updatePayload.purchase_price = itemData?.purchasePrice;
      if (itemData?.salePrice !== undefined) updatePayload.price_euros = itemData?.salePrice;
      if (itemData?.priceEuros !== undefined) updatePayload.price_euros = itemData?.priceEuros;
      if (itemData?.stockQuantity !== undefined) updatePayload.stock_quantity = itemData?.stockQuantity;
      if (itemData?.minStock !== undefined) updatePayload.min_stock = itemData?.minStock;
      if (itemData?.reorderThreshold !== undefined) updatePayload.reorder_threshold = itemData?.reorderThreshold;
      if (itemData?.location !== undefined) updatePayload.location = itemData?.location;
      if (itemData?.supplierId !== undefined) updatePayload.supplier_id = itemData?.supplierId || null;
      if (itemData?.status !== undefined) updatePayload.status = itemData?.status;
      if (itemData?.archivedAt !== undefined) updatePayload.archived_at = itemData?.archivedAt;
      updatePayload.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('barrel_inventory')
        ?.update(updatePayload)
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating barrel inventory item:', error);
      throw error;
    }
  },

  // ===================== CARTON INVENTORY =====================

  async getCartonInventory() {
    try {
      const { data, error } = await supabase
        ?.from('carton_inventory')
        ?.select('*, carton_suppliers(id, name)')
        ?.order('carton_type', { ascending: true });
      if (error) throw error;
      return data?.map(item => ({
        id: item?.id,
        name: item?.name || item?.carton_type,
        cartonType: item?.carton_type,
        category: item?.category || 'carton',
        sku: item?.sku,
        dimensions: item?.dimensions,
        description: item?.description,
        imageUrl: item?.image_url,
        purchasePrice: item?.purchase_price || item?.unit_price_buy || 0,
        salePrice: item?.unit_price_sell || 0,
        unitPriceBuy: item?.unit_price_buy || 0,
        unitPriceSell: item?.unit_price_sell || 0,
        stockQuantity: item?.stock_quantity || 0,
        minStockLevel: item?.min_stock_level || 10,
        minStock: item?.min_stock_level || 10,
        reorderThreshold: item?.reorder_threshold || item?.min_stock_level || 10,
        location: item?.location || 'Entrepôt principal',
        supplierId: item?.supplier_id,
        supplierName: item?.carton_suppliers?.name,
        notes: item?.notes,
        status: item?.status || 'active',
        archivedAt: item?.archived_at,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at,
        tableSource: 'carton_inventory'
      })) || [];
    } catch (error) {
      console.error('Error fetching carton inventory:', error);
      throw error;
    }
  },

  async createCartonInventoryItem(itemData) {
    try {
      const { data, error } = await supabase
        ?.from('carton_inventory')
        ?.insert([{
          name: itemData?.name,
          carton_type: itemData?.cartonType || itemData?.name,
          category: itemData?.category || 'carton',
          sku: itemData?.sku,
          dimensions: itemData?.dimensions,
          description: itemData?.description,
          image_url: itemData?.imageUrl,
          purchase_price: itemData?.purchasePrice || 0,
          unit_price_buy: itemData?.purchasePrice || 0,
          unit_price_sell: itemData?.salePrice || 0,
          stock_quantity: itemData?.stockQuantity || 0,
          min_stock_level: itemData?.minStock || 10,
          reorder_threshold: itemData?.reorderThreshold || itemData?.minStock || 10,
          location: itemData?.location || 'Entrepôt principal',
          supplier_id: itemData?.supplierId || null,
          notes: itemData?.notes,
          status: itemData?.status || 'active'
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating carton inventory item:', error);
      throw error;
    }
  },

  async updateCartonInventoryItem(id, itemData) {
    try {
      const updatePayload = {};
      if (itemData?.name !== undefined) updatePayload.name = itemData?.name;
      if (itemData?.cartonType !== undefined) updatePayload.carton_type = itemData?.cartonType;
      if (itemData?.category !== undefined) updatePayload.category = itemData?.category;
      if (itemData?.sku !== undefined) updatePayload.sku = itemData?.sku;
      if (itemData?.dimensions !== undefined) updatePayload.dimensions = itemData?.dimensions;
      if (itemData?.description !== undefined) updatePayload.description = itemData?.description;
      if (itemData?.imageUrl !== undefined) updatePayload.image_url = itemData?.imageUrl;
      if (itemData?.purchasePrice !== undefined) {
        updatePayload.purchase_price = itemData?.purchasePrice;
        updatePayload.unit_price_buy = itemData?.purchasePrice;
      }
      if (itemData?.salePrice !== undefined) updatePayload.unit_price_sell = itemData?.salePrice;
      if (itemData?.stockQuantity !== undefined) updatePayload.stock_quantity = itemData?.stockQuantity;
      if (itemData?.minStock !== undefined) updatePayload.min_stock_level = itemData?.minStock;
      if (itemData?.reorderThreshold !== undefined) updatePayload.reorder_threshold = itemData?.reorderThreshold;
      if (itemData?.location !== undefined) updatePayload.location = itemData?.location;
      if (itemData?.supplierId !== undefined) updatePayload.supplier_id = itemData?.supplierId || null;
      if (itemData?.notes !== undefined) updatePayload.notes = itemData?.notes;
      if (itemData?.status !== undefined) updatePayload.status = itemData?.status;
      if (itemData?.archivedAt !== undefined) updatePayload.archived_at = itemData?.archivedAt;
      updatePayload.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('carton_inventory')
        ?.update(updatePayload)
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating carton inventory item:', error);
      throw error;
    }
  },

  // ===================== ARCHIVE / DELETE =====================

  async checkItemUsage(itemId, tableSource) {
    try {
      let hasUsage = false;
      if (tableSource === 'carton_inventory') {
        const { data: sales } = await supabase?.from('carton_sales')?.select('id')?.eq('carton_inventory_id', itemId)?.limit(1);
        const { data: loadings } = await supabase?.from('truck_loading_items')?.select('id')?.eq('carton_inventory_id', itemId)?.limit(1);
        const { data: movements } = await supabase?.from('inventory_movements')?.select('id')?.eq('item_id', itemId)?.limit(1);
        hasUsage = (sales?.length > 0) || (loadings?.length > 0) || (movements?.length > 0);
      } else if (tableSource === 'barrel_inventory') {
        const { data: movements } = await supabase?.from('inventory_movements')?.select('id')?.eq('item_id', itemId)?.limit(1);
        hasUsage = movements?.length > 0;
      }
      return hasUsage;
    } catch (error) {
      console.error('Error checking item usage:', error);
      return true; // Safe default: assume used
    }
  },

  async archiveItem(itemId, tableSource) {
    try {
      const table = tableSource === 'barrel_inventory' ? 'barrel_inventory' : 'carton_inventory';
      const { error } = await supabase
        ?.from(table)
        ?.update({ status: 'archived', archived_at: new Date()?.toISOString(), updated_at: new Date()?.toISOString() })
        ?.eq('id', itemId);
      if (error) throw error;
      return { success: true, action: 'archived' };
    } catch (error) {
      console.error('Error archiving item:', error);
      throw error;
    }
  },

  async deleteItem(itemId, tableSource) {
    try {
      const table = tableSource === 'barrel_inventory' ? 'barrel_inventory' : 'carton_inventory';
      // Delete movements first
      await supabase?.from('inventory_movements')?.delete()?.eq('item_id', itemId);
      const { error } = await supabase?.from(table)?.delete()?.eq('id', itemId);
      if (error) throw error;
      return { success: true, action: 'deleted' };
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  },

  // ===================== INVENTORY MOVEMENTS =====================

  async getMovements(itemId, tableSource) {
    try {
      const { data, error } = await supabase
        ?.from('inventory_movements')
        ?.select('*')
        ?.eq('item_id', itemId)
        ?.eq('item_table', tableSource)
        ?.order('created_at', { ascending: false })
        ?.limit(50);
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching movements:', error);
      return [];
    }
  },

  async createMovement(movementData) {
    try {
      const { data, error } = await supabase
        ?.from('inventory_movements')
        ?.insert([{
          item_id: movementData?.itemId,
          item_table: movementData?.itemTable || 'carton_inventory',
          movement_type: movementData?.movementType,
          quantity: movementData?.quantity,
          previous_stock: movementData?.previousStock || 0,
          new_stock: movementData?.newStock || 0,
          source_module: movementData?.sourceModule,
          source_id: movementData?.sourceId || null,
          user_id: movementData?.userId || null,
          comment: movementData?.comment
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating movement:', error);
      throw error;
    }
  },

  // ===================== SUPPLIERS =====================

  async getSuppliers() {
    try {
      const { data, error } = await supabase
        ?.from('carton_suppliers')
        ?.select('id, name, supplier_type, contact_name, phone, email, city, country, is_active')
        ?.eq('is_active', true)
        ?.order('name', { ascending: true });
      if (error) throw error;
      return data?.map(s => ({
        id: s?.id,
        name: s?.name,
        supplierType: s?.supplier_type,
        contactName: s?.contact_name,
        phone: s?.phone,
        email: s?.email,
        city: s?.city,
        country: s?.country,
        isActive: s?.is_active
      })) || [];
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      return [];
    }
  },

  // ===================== ORDERS FOR ITEM =====================

  async getOrdersForItem(itemId, tableSource) {
    try {
      if (tableSource === 'carton_inventory') {
        const { data, error } = await supabase
          ?.from('carton_sales')
          ?.select('*')
          ?.eq('carton_inventory_id', itemId)
          ?.order('created_at', { ascending: false })
          ?.limit(20);
        if (error) throw error;
        return data || [];
      } else if (tableSource === 'barrel_inventory') {
        const { data, error } = await supabase
          ?.from('barrel_sales')
          ?.select('*')
          ?.order('created_at', { ascending: false })
          ?.limit(20);
        if (error) throw error;
        return data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching orders for item:', error);
      return [];
    }
  },

  // ===================== ANALYTICS FOR ITEM =====================

  async getAnalyticsForItem(itemId, tableSource) {
    try {
      const now = new Date();
      const d7 = new Date(now - 7 * 24 * 60 * 60 * 1000)?.toISOString();
      const d30 = new Date(now - 30 * 24 * 60 * 60 * 1000)?.toISOString();

      const { data: movements } = await supabase
        ?.from('inventory_movements')
        ?.select('*')
        ?.eq('item_id', itemId)
        ?.eq('item_table', tableSource)
        ?.order('created_at', { ascending: false });

      const sales7 = movements?.filter(m => m?.movement_type === 'sale' && m?.created_at >= d7) || [];
      const sales30 = movements?.filter(m => m?.movement_type === 'sale' && m?.created_at >= d30) || [];
      const totalUnits7 = sales7?.reduce((s, m) => s + Math.abs(m?.quantity || 0), 0);
      const totalUnits30 = sales30?.reduce((s, m) => s + Math.abs(m?.quantity || 0), 0);

      return {
        sales7Days: totalUnits7,
        sales30Days: totalUnits30,
        totalMovements: movements?.length || 0,
        recentMovements: movements?.slice(0, 10) || []
      };
    } catch (error) {
      console.error('Error fetching analytics for item:', error);
      return { sales7Days: 0, sales30Days: 0, totalMovements: 0, recentMovements: [] };
    }
  }
};

export default inventoryService;
